import { useMemo, useState } from 'react'
import type { CollectableItemRow } from '@klear/collection'
import { Link, useNavigate, useSearchParams } from 'react-router'
import {
  ROUTE_PAYMENT,
  PERMISSION_ACTION_FUND,
  PERMISSION_ENTITY_INVOICE_OFFER,
  FEE_ENTITY_TYPE_LABEL
} from '@klear/constants'
import { Button } from '@klear/ui/components/button'
import { ComboboxMulti } from '@klear/ui/components/combobox-multi'
import type { ComboboxOption } from '@klear/ui/components/combobox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useConfirmDialog } from '@klear/ui/components/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { useCollectionPaymentRetry, useRecoveryMutations } from '@klear/ui/hooks/use-collections'
import { useTraders } from '@klear/ui/hooks/use-traders'
import { RescheduleRecoveryDialog } from '@/app/collections/RescheduleRecoveryDialog'
import CollectionsDueChart from '@/app/collections/CollectionsDueChart'
import { CalendarClock, MoreHorizontal, Plus, RotateCcw, Trash2 } from 'lucide-react'
import {
  PaymentTypeOptions,
  PAYMENT_TYPE_COLLECTION_FEE,
  PAYMENT_TYPE_COLLECTION_RECOVERY,
  type PaymentType
} from '@klear/payment/constants'
import {
  PAAS_SCOPE,
  PAAS_SCOPE_OPTIONS,
  paasScopeFilters,
  usePaasCollections,
  type PaasScope
} from '@klear/ui/hooks/use-paas'
import PaasPositionsGrid from '@/components/PaasPositionsGrid'
import SyncStripePaymentsButton from '@/components/SyncStripePaymentsButton'
import { AgGridReact } from 'ag-grid-react'
import {
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ValueFormatterParams,
  type ValueGetterParams,
  type ICellRendererParams
} from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import { trpc, type RouterOutputs } from '@klear/ui/lib/trpc'
import { KlearDate } from '@klear/date'
import { resolveDateDisplayInput } from '@klear/order-event/list-display'
import ViewIcon from '@/icons/ViewIcon'
import ExportIcon from '@/icons/ExportIcon'
import { byPaymentCreatedAtDesc } from '@/lib/payment-sort'
import { paymentCanRetry, paymentIsFailed, paymentIsScheduled } from '@klear/payment/status'
import {
  buildCollectDeepLinkHref,
  buildCollectStartHref,
  COLLECTION_TYPE,
  COLLECTION_STEP_ID,
  type CollectionStepId,
  type CollectionType
} from '@/app/collections/steps'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const COLLECT_BUTTONS: { collectionType: CollectionType; label: string; nextStep: CollectionStepId }[] = [
  { collectionType: COLLECTION_TYPE.FEES, label: 'Collect fees', nextStep: COLLECTION_STEP_ID.SELECT_TRADER },
  {
    collectionType: COLLECTION_TYPE.ADVANCE_RECOVERY,
    label: 'Collect advances',
    nextStep: COLLECTION_STEP_ID.SELECT_TRADER
  }
]

const COLLECTABLE_ITEM_TYPE = {
  FEE: 'fee',
  ADVANCE_RECOVERY: 'advance_recovery'
} as const

type CollectableItemType = (typeof COLLECTABLE_ITEM_TYPE)[keyof typeof COLLECTABLE_ITEM_TYPE]

type CollectionRow = RouterOutputs['collection']['listCollectionPayments'][number]

const COLLECTABLE_ITEM_LABELS: Record<CollectableItemType, string> = {
  [COLLECTABLE_ITEM_TYPE.FEE]: 'Fee',
  [COLLECTABLE_ITEM_TYPE.ADVANCE_RECOVERY]: 'Advance recovery'
}

const collectableItemTypeLabel = (row: CollectableItemRow): string => {
  if (row.type === COLLECTABLE_ITEM_TYPE.FEE && row.feeType != null) {
    return FEE_ENTITY_TYPE_LABEL[row.feeType] ?? row.feeType.replace(/_/g, ' ')
  }
  return COLLECTABLE_ITEM_LABELS[row.type]
}

const collectableDaysOverdue = (row: CollectableItemRow): number | null => {
  const date = resolveDateDisplayInput(row.dueDate)
  if (!date) return null
  return KlearDate.today().diff(date, 'day')
}

const PAYMENT_TAB_ALL = 'all'
const PAYMENT_TAB_PAAS = 'paas'

type CollectionFilterType =
  | typeof PAYMENT_TYPE_COLLECTION_FEE
  | typeof PAYMENT_TYPE_COLLECTION_RECOVERY
  | typeof PAYMENT_TAB_ALL
  | typeof PAYMENT_TAB_PAAS

const FILTER_OPTIONS: { value: CollectionFilterType; label: string }[] = [
  { value: PAYMENT_TAB_ALL, label: 'Collectable' },
  { value: PAYMENT_TYPE_COLLECTION_FEE, label: 'Fees' },
  { value: PAYMENT_TYPE_COLLECTION_RECOVERY, label: 'Advances' },
  { value: PAYMENT_TAB_PAAS, label: 'PaaS' }
]

const FILTER_QUERY_PARAM = 'filter'
const DEFAULT_FILTER: CollectionFilterType = PAYMENT_TAB_ALL
const COLLECTIONS_POLL_INTERVAL_MS = 8000

const COLLECTABLE_TABLE_FILTER = {
  ALL: 'all'
} as const

const DUE_TIMING_FILTER = {
  ALL: COLLECTABLE_TABLE_FILTER.ALL,
  PAST_DUE: 'past_due',
  FUTURE: 'future'
} as const

const TRADER_FILTER_MODE = {
  INCLUDE: 'include',
  EXCLUDE: 'exclude'
} as const

type DueTimingFilter = (typeof DUE_TIMING_FILTER)[keyof typeof DUE_TIMING_FILTER]
type CollectableTypeFilter = typeof COLLECTABLE_TABLE_FILTER.ALL | CollectableItemType
type TraderFilterMode = (typeof TRADER_FILTER_MODE)[keyof typeof TRADER_FILTER_MODE]

const COLLECTABLE_TYPE_OPTIONS: { value: CollectableTypeFilter; label: string }[] = [
  { value: COLLECTABLE_TABLE_FILTER.ALL, label: 'All types' },
  { value: COLLECTABLE_ITEM_TYPE.FEE, label: 'Fees' },
  { value: COLLECTABLE_ITEM_TYPE.ADVANCE_RECOVERY, label: 'Advances' }
]

const DUE_TIMING_OPTIONS: { value: DueTimingFilter; label: string }[] = [
  { value: DUE_TIMING_FILTER.ALL, label: 'All due dates' },
  { value: DUE_TIMING_FILTER.PAST_DUE, label: 'Past due' },
  { value: DUE_TIMING_FILTER.FUTURE, label: 'Future' }
]

const ROW_BG_SCHEDULED = 'rgb(254 249 195)'
const ROW_BG_FAILED = 'rgb(254 202 202)'

const isCollectionFilterType = (value: string | null): value is CollectionFilterType =>
  value != null && FILTER_OPTIONS.some((opt) => opt.value === value)

const positiveId = (id: number | null | undefined): number | undefined => (id != null && id > 0 ? id : undefined)

const collectHighlightReferenceId = (row: CollectableItemRow): number | undefined => {
  if (row.type === COLLECTABLE_ITEM_TYPE.FEE) {
    return positiveId(row.feeId) ?? positiveId(row.referenceId)
  }
  return positiveId(row.referenceId)
}

const isAdvanceRecoveryPastDue = (row: CollectableItemRow): boolean => {
  const date = resolveDateDisplayInput(row.dueDate)
  if (!date) return false
  return date.isBefore(KlearDate.today())
}

const isAdvanceRecoveryFuture = (row: CollectableItemRow): boolean => {
  const date = resolveDateDisplayInput(row.dueDate)
  if (!date) return false
  return !date.isBefore(KlearDate.today())
}

const matchesDueTimingFilter = (row: CollectableItemRow, filter: DueTimingFilter): boolean => {
  if (row.type === COLLECTABLE_ITEM_TYPE.FEE) return true
  if (filter === DUE_TIMING_FILTER.ALL) return true
  if (filter === DUE_TIMING_FILTER.PAST_DUE) return isAdvanceRecoveryPastDue(row)
  return isAdvanceRecoveryFuture(row)
}

const matchesCollectableTypeFilter = (row: CollectableItemRow, filter: CollectableTypeFilter): boolean => {
  if (filter === COLLECTABLE_TABLE_FILTER.ALL) return true
  return row.type === filter
}

const matchesTraderFilter = (row: CollectableItemRow, traderIds: number[], mode: TraderFilterMode): boolean => {
  if (traderIds.length === 0) return true
  if (row.type === COLLECTABLE_ITEM_TYPE.FEE) return mode === TRADER_FILTER_MODE.EXCLUDE
  const matches = traderIds.includes(row.traderId)
  return mode === TRADER_FILTER_MODE.INCLUDE ? matches : !matches
}

export default function Collections() {
  useDocumentTitle('Collections')
  const { user } = useAuth()
  const navigate = useNavigate()
  const agGrid = useKlearAgGrid()
  const [gridApi, setGridApi] = useState<GridApi<CollectionRow> | null>(null)
  const [rescheduleTarget, setRescheduleTarget] = useState<{
    offerId: number
    currentDate: KlearDate | null
  } | null>(null)
  const { cancelRecovery, isCanceling } = useRecoveryMutations()
  const { retryPayment, isRetrying } = useCollectionPaymentRetry()
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const [searchParams, setSearchParams] = useSearchParams()
  const [dueTimingFilter, setDueTimingFilter] = useState<DueTimingFilter>(DUE_TIMING_FILTER.ALL)
  const [paasScope, setPaasScope] = useState<PaasScope>(PAAS_SCOPE.OUTSTANDING)
  const [collectableTypeFilter, setCollectableTypeFilter] = useState<CollectableTypeFilter>(
    COLLECTABLE_TABLE_FILTER.ALL
  )
  const [traderFilterMode, setTraderFilterMode] = useState<TraderFilterMode>(TRADER_FILTER_MODE.INCLUDE)
  const [selectedTableTraders, setSelectedTableTraders] = useState<ComboboxOption[]>([])
  const { traders = [], isLoading: isTradersLoading } = useTraders()
  const filterParam = searchParams.get(FILTER_QUERY_PARAM)
  const activeFilter: CollectionFilterType = isCollectionFilterType(filterParam) ? filterParam : DEFAULT_FILTER

  const setActiveFilter = (value: CollectionFilterType) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === DEFAULT_FILTER) {
          next.delete(FILTER_QUERY_PARAM)
        } else {
          next.set(FILTER_QUERY_PARAM, value)
        }
        return next
      },
      { replace: true }
    )
  }

  const isAllTab = activeFilter === PAYMENT_TAB_ALL
  const isPaasTab = activeFilter === PAYMENT_TAB_PAAS

  const { data: allCollections, isLoading: isCollectionsLoading } = trpc.collection.listCollectionPayments.useQuery(
    {},
    {
      enabled: !isAllTab && !isPaasTab,
      refetchInterval: COLLECTIONS_POLL_INTERVAL_MS
    }
  )

  const { positions: paasPositions, isLoading: isPaasLoading } = usePaasCollections(
    paasScopeFilters(paasScope),
    isPaasTab
  )

  const { data: collectableItemsData, isLoading: isCollectableLoading } =
    trpc.collection.listAllCollectableItems.useQuery(
      {},
      {
        enabled: isAllTab,
        refetchInterval: COLLECTIONS_POLL_INTERVAL_MS
      }
    )
  const collectableItems = useMemo<CollectableItemRow[]>(
    () =>
      (collectableItemsData ?? []).filter(
        (row) => row.type === COLLECTABLE_ITEM_TYPE.FEE || row.type === COLLECTABLE_ITEM_TYPE.ADVANCE_RECOVERY
      ),
    [collectableItemsData]
  )

  const traderOptions: ComboboxOption[] = useMemo(
    () =>
      traders.map((trader) => ({
        value: `${trader.id}`,
        label: trader.name ?? 'Unknown'
      })),
    [traders]
  )

  const tableTraderIds = useMemo(
    () => selectedTableTraders.map((trader) => parseInt(trader.value, 10)).filter((id) => id > 0),
    [selectedTableTraders]
  )

  const filteredCollectableItems = useMemo(
    () =>
      collectableItems.filter(
        (row) =>
          matchesCollectableTypeFilter(row, collectableTypeFilter) &&
          matchesDueTimingFilter(row, dueTimingFilter) &&
          matchesTraderFilter(row, tableTraderIds, traderFilterMode)
      ),
    [collectableItems, collectableTypeFilter, dueTimingFilter, tableTraderIds, traderFilterMode]
  )

  const isLoading = isAllTab ? isCollectableLoading : isCollectionsLoading

  const collections = useMemo<CollectionRow[]>(() => {
    if (!allCollections) return []
    const filtered =
      activeFilter === PAYMENT_TAB_ALL
        ? allCollections
        : allCollections.filter((row) => row.payment.paymentType === activeFilter)
    return [...filtered].sort(byPaymentCreatedAtDesc)
  }, [allCollections, activeFilter])

  const recoveryOfferId = (row: CollectionRow): number | null => {
    if (row.payment.paymentType !== PAYMENT_TYPE_COLLECTION_RECOVERY) return null
    const id = row.orderEvent?.ids?.[0]
    return id != null && id > 0 ? id : null
  }

  const handleRetryPayment = (paymentId: number) => {
    confirm({
      title: 'Retry payment?',
      description: 'This resends the failed collection to the payment provider using its existing details.',
      onConfirm: async () => {
        await retryPayment({ id: paymentId })
      }
    })
  }

  const handleCancelRecovery = (offerId: number) => {
    confirm({
      title: 'Cancel recovery?',
      description: 'This removes the scheduled advance recovery for this offer. This action cannot be undone.',
      onConfirm: async () => {
        await cancelRecovery({ offerId })
      }
    })
  }

  const ActionCellRenderer = (props: ICellRendererParams<CollectionRow>) => {
    const row = props.data
    if (!row) return null

    const offerId = recoveryOfferId(row)
    const modifiableOfferId = offerId != null && paymentIsScheduled(row.payment.status) ? offerId : null
    const canRetry = paymentCanRetry(row.payment)

    if (modifiableOfferId == null && !canRetry) {
      return (
        <Link
          to={`/${ROUTE_PAYMENT}/${row.payment.id}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ViewIcon />
          View
        </Link>
      )
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link to={`/${ROUTE_PAYMENT}/${row.payment.id}`} className="flex items-center gap-2">
              <ViewIcon />
              View
            </Link>
          </DropdownMenuItem>
          {canRetry && (
            <DropdownMenuItem
              onClick={() => handleRetryPayment(row.payment.id)}
              disabled={isRetrying}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Retry payment
            </DropdownMenuItem>
          )}
          {modifiableOfferId != null && (
            <>
              <DropdownMenuItem
                onClick={() =>
                  setRescheduleTarget({ offerId: modifiableOfferId, currentDate: row.payment.effectiveDate ?? null })
                }
                className="flex items-center gap-2"
              >
                <CalendarClock className="h-4 w-4" />
                Reschedule
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleCancelRecovery(modifiableOfferId)}
                disabled={isCanceling}
                className="flex items-center gap-2 text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Cancel recovery
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const buildCollectHref = (row: CollectableItemRow): string => {
    const collectionType =
      row.type === COLLECTABLE_ITEM_TYPE.FEE ? COLLECTION_TYPE.FEES : COLLECTION_TYPE.ADVANCE_RECOVERY
    return buildCollectDeepLinkHref({
      collectionType,
      nextStep: COLLECTION_STEP_ID.SELECT_ITEMS,
      referenceId: collectHighlightReferenceId(row),
      traderId: positiveId(row.traderId)
    })
  }

  const CollectableReferenceCellRenderer = (props: ICellRendererParams<CollectableItemRow>) => {
    const row = props.data
    if (!row?.referenceDisplay || row.referenceDisplay === '-') return '-'
    return row.referenceDisplay
  }

  const CollectableActionCellRenderer = (props: ICellRendererParams<CollectableItemRow>) => {
    const row = props.data
    if (!row) return null
    return (
      <Link
        to={buildCollectHref(row)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        Collect
      </Link>
    )
  }

  const columnDefs: ColDef<CollectionRow>[] = [
    {
      headerName: 'ID',
      colId: 'id',
      filter: 'agTextColumnFilter',
      valueGetter: (params: ValueGetterParams<CollectionRow>) => {
        const row = params.data
        if (!row) return ''
        const paymentType = row.payment.paymentType
        if (
          (paymentType === PAYMENT_TYPE_COLLECTION_FEE || paymentType === PAYMENT_TYPE_COLLECTION_RECOVERY) &&
          row.orderEvent
        ) {
          return row.orderEvent.idDisplay
        }
        return row.payment.id ?? ''
      },
      width: 120,
      minWidth: 120
    },
    {
      headerName: 'Date',
      colId: 'effectiveDate',
      field: 'payment.effectiveDate'
    },
    {
      headerName: 'Amount',
      colId: 'amount',
      field: 'payment.amount'
    },
    {
      headerName: 'Type',
      colId: 'paymentType',
      filter: 'agTextColumnFilter',
      valueGetter: (params: ValueGetterParams<CollectionRow>) => {
        const type = params.data?.payment.paymentType
        if (type == null) return ''
        return PaymentTypeOptions.find((opt) => opt.value === (type as PaymentType))?.label ?? type
      }
    },
    {
      headerName: 'Customer',
      colId: 'customerName',
      field: 'orderEvent.customerName',
      valueFormatter: (params: ValueFormatterParams<CollectionRow>) => (params.value as string) || '-'
    },
    {
      headerName: 'From',
      colId: 'sourceAccountOrganizationName',
      field: 'payment.sourceAccountOrganizationName'
    },
    {
      headerName: 'To',
      colId: 'destinationAccountOrganizationName',
      field: 'payment.destinationAccountOrganizationName'
    },
    {
      headerName: 'Status',
      colId: 'status',
      field: 'payment.status'
    },
    {
      headerName: 'Created by',
      colId: 'createdByEmail',
      field: 'payment.createdByEmail'
    },
    {
      headerName: '',
      colId: 'actions',
      type: 'actions',
      pinned: 'right',
      cellStyle: { overflow: 'visible' },
      width: 80,
      minWidth: 80,
      maxWidth: 80,
      cellRenderer: ActionCellRenderer
    }
  ]

  const collectableColumnDefs: ColDef<CollectableItemRow>[] = [
    {
      headerName: 'Type',
      colId: 'type',
      filter: 'agTextColumnFilter',
      valueGetter: (params: ValueGetterParams<CollectableItemRow>) =>
        params.data ? collectableItemTypeLabel(params.data) : '',
      width: 180,
      minWidth: 160
    },
    {
      headerName: 'Trader',
      colId: 'traderOrganizationName',
      field: 'traderOrganizationName'
    },
    {
      headerName: 'Customer',
      colId: 'customerName',
      field: 'customerName',
      valueFormatter: (params: ValueFormatterParams<CollectableItemRow>) => (params.value as string) || '-'
    },
    {
      headerName: 'Reference',
      colId: 'referenceDisplay',
      field: 'referenceDisplay',
      cellRenderer: CollectableReferenceCellRenderer
    },
    {
      headerName: 'Total',
      colId: 'totalAmount',
      field: 'totalAmount',
      type: 'rightAligned'
    },
    {
      headerName: 'Remaining',
      colId: 'amount',
      field: 'amount',
      type: 'rightAligned'
    },
    {
      headerName: 'Overdue',
      colId: 'overdue',
      valueGetter: (params: ValueGetterParams<CollectableItemRow>) =>
        params.data ? collectableDaysOverdue(params.data) : null,
      valueFormatter: (params: ValueFormatterParams<CollectableItemRow, number | null>) =>
        params.value != null ? `${params.value}` : '-',
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      width: 110,
      minWidth: 100
    },
    {
      headerName: 'Due date',
      colId: 'dueDate',
      field: 'dueDate'
    },
    {
      headerName: '',
      colId: 'actions',
      type: 'actions',
      pinned: 'right',
      cellStyle: { overflow: 'visible' },
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      cellRenderer: CollectableActionCellRenderer
    }
  ]

  const defaultColDef = {
    ...agGrid.defaultColDef,
    flex: 1
  }

  const onGridReady = (params: GridReadyEvent<CollectionRow>) => {
    setGridApi(params.api)
  }

  const onCollectableGridReady = (params: GridReadyEvent<CollectableItemRow>) => {
    setGridApi(params.api as unknown as GridApi<CollectionRow>)
  }

  const handleExport = () => {
    if (!gridApi) return
    if (isAllTab) {
      gridApi.exportDataAsCsv({
        fileName: `collectable_items_export_${KlearDate.today().toJSON()}.csv`,
        columnKeys: [
          'type',
          'traderOrganizationName',
          'customerName',
          'referenceDisplay',
          'totalAmount',
          'amount',
          'overdue',
          'dueDate'
        ]
      })
      return
    }
    gridApi.exportDataAsCsv({
      fileName: `collections_export_${KlearDate.today().toJSON()}.csv`,
      columnKeys: [
        'id',
        'effectiveDate',
        'amount',
        'paymentType',
        'customerName',
        'sourceAccountOrganizationName',
        'destinationAccountOrganizationName',
        'status',
        'createdByEmail'
      ]
    })
  }

  return (
    <div className="container mx-auto px-4 py-4 h-full flex flex-col gap-4 min-h-0">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">Collections</h2>
      </div>
      <CollectionsDueChart />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {FILTER_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={activeFilter === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <SyncStripePaymentsButton />
          <Button
            onClick={handleExport}
            variant="outline"
            disabled={!gridApi || (isAllTab ? filteredCollectableItems.length === 0 : collections.length === 0)}
          >
            <ExportIcon />
            <span>Export CSV</span>
          </Button>
          {user?.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_FUND) &&
            COLLECT_BUTTONS.map(({ collectionType, label, nextStep }) => (
              <Button
                key={collectionType}
                onClick={() => navigate(buildCollectStartHref(collectionType, nextStep))}
                variant="default"
              >
                <Plus className="w-4 h-4 mr-2" />
                {label}
              </Button>
            ))}
        </div>
      </div>
      {isPaasTab && (
        <div className="flex items-center gap-1">
          {PAAS_SCOPE_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              variant={paasScope === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPaasScope(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      )}
      {isAllTab && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            {COLLECTABLE_TYPE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={collectableTypeFilter === opt.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCollectableTypeFilter(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {DUE_TIMING_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={dueTimingFilter === opt.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDueTimingFilter(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <Select
            value={traderFilterMode}
            onValueChange={(value) => setTraderFilterMode(value as TraderFilterMode)}
            disabled={selectedTableTraders.length === 0}
          >
            <SelectTrigger className="w-[120px]" aria-label="Trader filter mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TRADER_FILTER_MODE.INCLUDE}>Include</SelectItem>
              <SelectItem value={TRADER_FILTER_MODE.EXCLUDE}>Exclude</SelectItem>
            </SelectContent>
          </Select>
          <ComboboxMulti
            options={traderOptions}
            value={selectedTableTraders}
            onChange={setSelectedTableTraders}
            triggerLabel="All traders"
            className="w-[240px]"
            size="sm"
            disabled={isTradersLoading}
          />
          <span className="text-xs text-muted-foreground">Due date filter applies to advances only.</span>
        </div>
      )}
      {isPaasTab ? (
        <PaasPositionsGrid positions={paasPositions} isLoading={isPaasLoading} variant="collections" />
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="w-full h-full min-h-[600px]">
            {isLoading ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">Loading...</div>
            ) : isAllTab ? (
              filteredCollectableItems.length === 0 ? (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No collectable items.
                </div>
              ) : (
                <AgGridReact<CollectableItemRow>
                  key="collectable-items-grid"
                  rowHeight={35}
                  headerHeight={40}
                  rowData={filteredCollectableItems}
                  columnDefs={collectableColumnDefs}
                  defaultColDef={defaultColDef}
                  columnTypes={agGrid.columnTypes}
                  dataTypeDefinitions={agGrid.dataTypeDefinitions}
                  animateRows={true}
                  enableCellTextSelection={false}
                  pagination={true}
                  paginationPageSize={20}
                  theme={agGrid.theme}
                  quickFilterText=""
                  onGridReady={onCollectableGridReady}
                />
              )
            ) : collections.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">No collections found.</div>
            ) : (
              <AgGridReact<CollectionRow>
                key="collections-grid"
                rowHeight={35}
                headerHeight={40}
                rowData={collections}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                columnTypes={agGrid.columnTypes}
                dataTypeDefinitions={agGrid.dataTypeDefinitions}
                animateRows={true}
                enableCellTextSelection={false}
                pagination={true}
                paginationPageSize={20}
                theme={agGrid.theme}
                quickFilterText=""
                onGridReady={onGridReady}
                getRowStyle={(params) => {
                  const status = params.data?.payment?.status
                  if (!status) return undefined
                  if (paymentIsScheduled(status)) return { backgroundColor: ROW_BG_SCHEDULED }
                  if (paymentIsFailed(status)) return { backgroundColor: ROW_BG_FAILED }
                  return undefined
                }}
              />
            )}
          </div>
        </div>
      )}

      {rescheduleTarget != null && (
        <RescheduleRecoveryDialog
          offerId={rescheduleTarget.offerId}
          currentDate={rescheduleTarget.currentDate}
          open={rescheduleTarget != null}
          onOpenChange={(open) => {
            if (!open) setRescheduleTarget(null)
          }}
        />
      )}
      {ConfirmDialog}
    </div>
  )
}
